// Fase 3 · Lógica pura de sincronización del TPV (sin React/Redux) para
// poder testearla: coalescer de peticiones y merge de mesas que conserva
// la identidad de los objetos que no cambian.

/**
 * Ejecuta `run(arg)` como mucho una vez a la vez. Si se pide otra
 * ejecución mientras hay una en vuelo, NO se descarta: se programa
 * exactamente UNA más al terminar (con el último argumento pedido).
 * Así un evento que llega durante un fetch no se pierde, y una ráfaga
 * de N eventos produce como máximo 2 fetches.
 */
export function createCoalescingRunner(run) {
  let inFlight = null;
  let pending = false;
  let pendingArg;

  const start = (arg) => {
    inFlight = Promise.resolve()
      .then(() => run(arg))
      .catch(() => undefined)
      .finally(() => {
        inFlight = null;
        if (pending) {
          pending = false;
          const next = pendingArg;
          pendingArg = undefined;
          start(next);
        }
      });
    return inFlight;
  };

  return {
    request(arg) {
      if (inFlight) {
        pending = true;
        pendingArg = arg;
        return inFlight;
      }
      return start(arg);
    },
    isInFlight: () => inFlight !== null,
    hasPending: () => pending,
  };
}

const isPlainObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);

/** Igualdad superficial (1 nivel; arrays/objetos anidados por referencia o JSON corto). */
export function shallowEqualMesa(a, b) {
  if (a === b) return true;
  if (!isPlainObject(a) || !isPlainObject(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    const va = a[k];
    const vb = b[k];
    if (va === vb) continue;
    // Los DTO del hub llegan como objetos nuevos: comparamos anidados por
    // valor (son pequeños: zona, pedidos resumidos, etc.).
    if (typeof va === "object" && typeof vb === "object" && va && vb) {
      try {
        if (JSON.stringify(va) === JSON.stringify(vb)) continue;
      } catch {
        return false;
      }
    }
    return false;
  }
  return true;
}

/**
 * Devuelve la lista `next` reutilizando los objetos de `prev` cuyos datos
 * no han cambiado (misma `id` y igualdad superficial). Si no cambia nada,
 * devuelve `prev` tal cual. Permite que el memo de las fichas de mesa no
 * repinte todo el plano en cada evento.
 */
export function mergeMesasKeepingIdentity(prev, next) {
  if (!Array.isArray(next)) return prev;
  if (!Array.isArray(prev) || prev.length === 0) return next;
  const byId = new Map(prev.map((m) => [m?.id, m]));
  let changed = prev.length !== next.length;
  const merged = next.map((m, i) => {
    const old = byId.get(m?.id);
    if (old && shallowEqualMesa(old, m)) {
      if (prev[i] !== old) changed = true;
      return old;
    }
    changed = true;
    return m;
  });
  return changed ? merged : prev;
}

/**
 * Decide qué recargar tras un lote de eventos `products/restaurante/{id}`
 * ({ productoId, action }). Borrados con id → parche local; cualquier otro
 * cambio de producto → una recarga de productos; bulk/sin acción → además
 * categorías, tags y frecuentes.
 */
export function planProductHubRefresh(events) {
  const deletedIds = [];
  let refetchProducts = false;
  let refetchCatalogExtras = false;
  for (const e of events || []) {
    const action = e?.action;
    if (!action || action === "bulk") {
      refetchProducts = true;
      refetchCatalogExtras = true;
    } else if (action === "deleted" && e?.productoId != null) {
      deletedIds.push(e.productoId);
    } else {
      refetchProducts = true;
    }
  }
  return { deletedIds: [...new Set(deletedIds)], refetchProducts, refetchCatalogExtras };
}

/**
 * Campos de la mesa que pertenecen a la cuenta (sesión) y que el servidor
 * pone a cero al cobrarla o cerrarla (MesaRepository.ResetAfterPaymentAsync).
 * Un único sitio para que el cierre optimista deje la ficha igual que la
 * dejará el servidor: antes no se tocaban `comensales` y compañía, y el
 * dispositivo que cobraba veía la mesa libre con los comensales anteriores
 * (su propio `mesa.changed` llega como eco).
 */
export function freedMesaFields(now = new Date()) {
  return {
    cuenta: 0,
    ocupada: false,
    sessionId: null,
    sessionCreated: null,
    pedidos: 0,
    comensales: 0,
    requestPayment: false,
    requestCamarero: false,
    checkPrintedAt: null,
    modificationDate: now,
  };
}
